import React, { useEffect, useMemo, useState } from 'react';
import { Button, Row, Space, Table, Tag, Col, Spin } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { JobsFilter, JobStatus } from './JobsFilter';
import type { ColumnsType } from 'antd/es/table';
import { useGetTripsHistoryByDriverIdQuery } from '../../../../GraphQL/hooks/useGetTripsHistoryByDriverIdQuery';
import { useGetAllJobsCanceledByTaxiDriverIdQuery } from '../../../../GraphQL/hooks/useGetAllJobsCanceledByTaxiDriverIdQuery';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// Extend dayjs with isBetween plugin
dayjs.extend(isBetween);

interface Job {
    id?: string;
    logicalId?: string;
    date: string;
    riderName: string;
    status: 'Completed' | 'Canceled' | string;
    reviewCompliment?: string;
    reviewRating?: number;
    document?: string;
    canceledBy?: string;
    rawDate?: number;
}

interface JobsHistoryProps {
    driverId?: string;
    open: boolean;
    handleViewInvoice: (job: Job) => void;
}

const JobsHistory: React.FC<JobsHistoryProps> = ({ driverId, open, handleViewInvoice }) => {
    const [currentPage, setCurrentPage] = useState(1);
    // Local state for filter
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf('month'),
        dayjs().endOf('month'),
    ]);
    const [quickStatusFilter, setQuickStatusFilter] = useState<JobStatus>('All');

    useEffect(() => {
        if (!open) {
            setCurrentPage(1);
            setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
            setQuickStatusFilter('All');
        }
    }, [open]);

    const pageSize = 10;
    const { data: jobsData, loading: loadingjobs } = useGetTripsHistoryByDriverIdQuery({
        id: driverId,
        invoiceType: 'taxi_mobility',
        page: currentPage,
        limit: pageSize,
    });

    const { data: canceledJobsData, loading: loadingCanceldJobs } = useGetAllJobsCanceledByTaxiDriverIdQuery({
        id: driverId,
        page: currentPage,
        limit: pageSize,
    });

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'Completed':
                return <Tag color="green">Completed</Tag>;
            case 'Canceled':
                return <Tag color="red">Canceled</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const jobsHistory: Job[] = useMemo(() => {
        const trips = jobsData?.data ?? [];
        const canceledTrips = canceledJobsData?.data ?? [];

        // Map completed trips
        const mappedTrips: Job[] = trips.map((trip: any, index: number) => {
            const timestamp = trip.date ? Number(trip.date) : undefined;

            return {
                id: trip._id ?? `trip-${index}`,
                date: timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
                riderName: trip.riderFullName ?? '',
                reviewCompliment: trip.reviewCompliment,
                reviewRating: trip.reviewRating,
                status: trip.status === 'Closed' ? 'Completed' : trip.status,
                document: trip.invoiceAttachment?.url,
                rawDate: timestamp,
                canceledBy: trip.canceledBy,
            };
        });

        // Map canceled trips
        const mappedCanceled: Job[] = canceledTrips.map((trip: any, index: number) => {
            const timestamp = trip.date ? Number(trip.date) : undefined;

            return {
                id: `Trip Canceled-${trip.logicalId}`,
                date: timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
                riderName: trip.riderName,
                status: 'Canceled',
                rawDate: timestamp,
                canceledBy: trip.canceledBy,
            };
        });

        // Merge both arrays
        const allJobs = [...mappedTrips, ...mappedCanceled];

        //  Apply filters
        return allJobs.filter((job) => {
            const inDateRange = !job.rawDate || dayjs(job.rawDate).isBetween(dateRange[0], dateRange[1], 'day', '[]');

            const statusMatch = quickStatusFilter === 'All' || job.status === quickStatusFilter;

            return inDateRange && statusMatch;
        });
    }, [jobsData, canceledJobsData, dateRange, quickStatusFilter]);

    const completedTotal = jobsData?.pagination?.totalCount ?? 0;
    const canceledTotal = canceledJobsData?.pagination?.totalCount ?? 0;

    let totalTrips = 0;

    if (quickStatusFilter === 'Completed') {
        totalTrips = completedTotal;
    } else if (quickStatusFilter === 'Canceled') {
        totalTrips = canceledTotal;
    } else {
        totalTrips = completedTotal + canceledTotal;
    }

    const handleView = (job: Job) => {
        if (job.document) {
            window.open(job.document, '_blank', 'noopener,noreferrer');
        } else {
            alert('Document not available');
        }
    };

    const columns: ColumnsType<Job> = [
        {
            title: 'Trip ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Date',
            dataIndex: 'rawDate',
            key: 'date',
            render: (rawDate: number | undefined) => (rawDate ? dayjs(rawDate).format('YYYY-MM-DD HH:mm') : ''),
        },
        {
            title: 'Rider Name',
            dataIndex: 'riderName',
            key: 'riderName',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, record) => (
                <div>
                    {getStatusTag(record.status)}
                    {record.status === 'Completed' && (record.reviewRating || record.reviewCompliment) && (
                        <div style={{ fontSize: '12px', color: 'blue' }}>
                            {[record.reviewRating ? `${record.reviewRating} ⭐` : null, record.reviewCompliment]
                                .filter(Boolean)
                                .join(' ')}
                        </div>
                    )}
                    {record.status === 'Canceled' && record.canceledBy && (
                        <div style={{ fontSize: '12px', color: 'red' }}>Cancelled by {record.canceledBy}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'Document',
            key: 'invoice',
            align: 'center',
            render: (_, record) =>
                record.status === 'Canceled' ? (
                    <span style={{ color: 'gray' }}>N/A</span>
                ) : (
                    <Space>
                        <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
                    </Space>
                ),
        },
    ];

    return (
        <div style={{ flex: '1 1 auto' }}>
            <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                <Col>
                    <JobsFilter
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        quickStatusFilter={quickStatusFilter}
                        setQuickStatusFilter={setQuickStatusFilter}
                    />
                </Col>
            </Row>
            <Spin spinning={loadingjobs || loadingCanceldJobs}>
                <Table<Job>
                    dataSource={jobsHistory}
                    columns={columns}
                    rowKey={(record) => record.id || record.logicalId || ''}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalTrips,
                        onChange: (page) => setCurrentPage(page),
                        showTotal: (total) => `Total Trips: ${total}`,
                        showSizeChanger: false,
                    }}
                    bordered
                    size="middle"
                />
            </Spin>
        </div>
    );
};

export default JobsHistory;
